import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import SidebarLayout from 'components/Layout/Layout';
import { API_URL } from 'env';
import { Modal, Select } from "antd";
import TinyMce from 'components/GbCpn/TinyMce';

const { Option } = Select;

type LocaleText = { en: string; vi: string }

const EditDailyReading: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [locale, setLocale] = useState<string>('en')
  const [form, setForm] = useState({
    title: {
      en: '',
      vi: '',
    },
    type: '',
    number: 0,
    description: {
      en: '',
      vi: '',
    },
    is_active: false,
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    // Load dữ liệu theo id
    const fetchData = async () => {
      try {
        setLoadingData(true);
        const response = await fetch(`${API_URL}api/v1/be/daily-index/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const json = await response.json();

        let titleParse: LocaleText;
        let descriptionParse: LocaleText;

        try {
          const titleParsed = JSON.parse(json.data.title);
          if (titleParsed && typeof titleParsed === "object" && ("en" in titleParsed || "vi" in titleParsed)) {
            titleParse = titleParsed;
          } else {
            titleParse = { en: json.data.title, vi: '' };
          }
        } catch {
          titleParse = { en: json.data.title, vi: '' };
        }

        try {
          const desParsed = JSON.parse(json.data.description);
          if (desParsed && typeof desParsed === "object" && ("en" in desParsed || "vi" in desParsed)) {
            descriptionParse = desParsed;
          } else {
            descriptionParse = { en: json.data.description, vi: '' };
          }
        } catch {
          descriptionParse = { en: json.data.description, vi: '' };
        }

        if (json.status) {
          setForm(json.data);
          setForm(prev => ({
            ...prev,
            title: titleParse
          }))
          setForm(prev => ({
            ...prev,
            description: descriptionParse
          }))
        } else {
          Modal.error({
            title: 'Thất bại',
            content: 'Lấy dữ liệu thất bại',
            onOk: () => navigate('/daily'),
          });
        }
      } catch (error: any) {
        Modal.error({
          title: 'Lỗi',
          content: 'Lỗi khi tải dữ liệu: ' + error.message,
          onOk: () => navigate('/daily'),
        });
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleSave = async () => {
    if (locale === 'en') {
      if (!form.title.en.trim()) {
        Modal.warning({ title: 'Thiếu dữ liệu', content: 'Vui lòng nhập tiêu đề' });
        return;
      }
      if (!form.type.trim()) {
        Modal.warning({ title: 'Thiếu dữ liệu', content: 'Vui lòng nhập type' });
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_URL}api/v1/be/daily-index/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(form),
        });

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const json = await response.json();

        if (json.status) {
          Modal.success({
            title: 'Thành công',
            content: 'Cập nhật thành công!',
            onOk: () => navigate('/daily'),
          });
        } else {
          Modal.error({
            title: 'Có lỗi',
            content: json.message || 'Unknown error',
          });
        }
      } catch (error: any) {
        Modal.error({
          title: 'Lỗi',
          content: 'Lỗi khi cập nhật: ' + error.message,
        });
      } finally {
        setLoading(false);
      }
    } else if (locale === 'vi') {
      if (!form.title.vi.trim()) {
        Modal.warning({ title: 'Thiếu dữ liệu', content: 'Vui lòng nhập tiêu đề' });
        return;
      }
      if (!form.type.trim()) {
        Modal.warning({ title: 'Thiếu dữ liệu', content: 'Vui lòng nhập type' });
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_URL}api/v1/be/daily-index/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(form),
        });

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const json = await response.json();

        if (json.status) {
          Modal.success({
            title: 'Thành công',
            content: 'Cập nhật thành công!',
            onOk: () => navigate('/daily'),
          });
        } else {
          Modal.error({
            title: 'Có lỗi',
            content: json.message || 'Unknown error',
          });
        }
      } catch (error: any) {
        Modal.error({
          title: 'Lỗi',
          content: 'Lỗi khi cập nhật: ' + error.message,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  if (loadingData) return <p>Đang tải dữ liệu...</p>;

  return (
    <SidebarLayout title={<h4 className="text-2xl font-bold !m-0 py-4">Chỉnh sửa Daily Reading</h4>}>
      <div className="p-4 bg-white rounded shadow">
        <h3 className="text-xl font-bold mb-4" style={{ color: '#5e5873' }}>
          Chỉnh sửa
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block font-semibold mb-1" style={{ color: '#5e5873', marginBottom: '10px' }}>
              Ngôn ngữ
            </label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="w-full border p-2 rounded"
              style={{ border: '1px solid #ccc' }}
            >
              <option value="vi">Vietnam</option>
              <option value="en">English</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block font-semibold mb-1" style={{ color: '#5e5873', marginBottom: '10px' }}>
              Title
            </label>
            <input
              type="text"
              value={locale === 'en' ? (form.title.en ?? '') : (form.title.vi ?? '')}
              onChange={(e) =>
                setForm({
                  ...form,
                  title: {
                    ...form.title,
                    [locale]: e.target.value,
                  },
                })
              }
              className="w-full border p-2 rounded"
              style={{ border: '1px solid #ccc' }}
            />
          </div>

          <div className="mb-4">
            <label className="block font-semibold mb-1" style={{ color: '#5e5873', marginBottom: '10px' }}>
              Type
            </label>
            <Select
              placeholder="Chọn một option"
              value={form.type}
              onChange={(e) =>
                setForm({
                  ...form,
                  type: e,
                })
              }
              style={{ width: "100%" }}
            >
              <Option value="emotion">Emotion</Option>
              <Option value="behavior">Behavior</Option>
              <Option value="risk">Risk</Option>
              <Option value="pattern">Pattern</Option>
              <Option value="practice">Practice</Option>
            </Select>
          </div>

          <div className="mb-4">
            <label className="block font-semibold mb-1" style={{ color: '#5e5873', marginBottom: '10px' }}>
              Number
            </label>
            <input
              type="number"
              min="1"
              max="9"
              value={form.number}
              onChange={(e) =>
                setForm({
                  ...form,
                  number: Number(e.target.value),
                })
              }
              className="w-full border p-2 rounded"
              style={{ border: '1px solid #ccc' }}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block font-semibold mb-1" style={{ color: '#5e5873', marginBottom: '10px' }}>
            Nội dung
          </label>
          <div className="border rounded" style={{ border: '1px solid #ccc' }}>
            {/* <Editor
              apiKey="88l5l3dt4pora3fh41n2fp8ucdppyofrsccqrart6ncvui8f"
              value={form.description}
              init={{
                height: 300,
                menubar: 'file edit view insert format tools table help',
                plugins: [
                  'advlist autolink lists link image charmap print preview anchor',
                  'searchreplace visualblocks code fullscreen',
                  'insertdatetime media table paste code help wordcount',
                ],
                toolbar:
                  'undo redo | formatselect | bold italic underline strikethrough | ' +
                  'alignleft aligncenter alignright alignjustify | ' +
                  'bullist numlist outdent indent | removeformat | help',
              }}
              onEditorChange={(content) => setForm({ ...form, description: content })}
            /> */}
            <TinyMce
              defaultValue={locale === 'en' ? form.description.en : form.description.vi}
              onChange={(value) =>
                setForm({
                  ...form,
                  description: {
                    ...form.description,
                    [locale]: value,
                  },
                })
              }
            />
          </div>
        </div>

        <div className="mb-4 flex items-center gap-2" style={{ color: '#5e5873' }}>
          <input
            style={{ width: '20px', height: '20px', border: '1px solid #ccc' }}
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          <label htmlFor="is_active" className="font-semibold">
            Trạng thái
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => navigate('/daily')}
            className="border px-4 py-2 rounded"
            disabled={loading}
          >
            Hủy
          </button>
          <button
            onClick={handleSave}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Đang xử lý...' : 'Lưu'}
          </button>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default EditDailyReading;
